import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import '../../styles/OfferedServices.css';

const OfferedServices = ({ schoolId }) => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // For adding a new service
    const [newService, setNewService] = useState({ serviceName: '', price: '', duration: '' });
    const [showAddForm, setShowAddForm] = useState(false);
    // For editing an existing service
    const [editServiceId, setEditServiceId] = useState(null);
    const [editService, setEditService] = useState({ serviceName: '', price: '', duration: '' });

    // Converts "1h 30min" -> "01:30" for the time input
    const parseHumanDuration = (humanDuration) => {
        if (!humanDuration) return '00:00';
        const match = humanDuration.match(/(\d+)h\s*(\d+)min/);
        if (!match) return '00:00';
        const hours = parseInt(match[1], 10) || 0;
        const minutes = parseInt(match[2], 10) || 0;
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };

    // Converts "HH:mm" -> "PT1H30M"
    const parseDuration = (time) => {
        if (!time) return 'PT0H0M';
        const parts = time.split(':');
        if (parts.length !== 2) return 'PT0H0M';
        const [hours, minutes] = parts.map(Number);
        if (isNaN(hours) || isNaN(minutes)) return 'PT0H0M';
        return `PT${hours}H${minutes}M`;
    };

    // Fetch services for the given school
    useEffect(() => {
        if (!schoolId) return;
        const token = localStorage.getItem('jwt');
        setLoading(true);
        axios
            .get(`/api/v1/services/school/${schoolId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then((response) => {
                setServices(response.data || []);
                setLoading(false);
            })
            .catch(() => {
                setError('Błąd pobierania usług.');
                setLoading(false);
            });
    }, [schoolId]);

    // Handler for adding a new service
    const handleAddService = async () => {
        if (!newService.serviceName || !newService.price || !newService.duration) {
            setError('Wszystkie pola są wymagane.');
            return;
        }

        const token = localStorage.getItem('jwt');
        const payload = {
            serviceName: newService.serviceName,
            price: parseFloat(newService.price),
            duration: parseDuration(newService.duration),
            schoolId,
        };
        try {
            const response = await axios.post('/api/v1/services', payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setServices([...services, response.data]);
            setNewService({ serviceName: '', price: '', duration: '' });
            setShowAddForm(false);
            setError('');
        } catch (err) {
            console.error('Błąd przy dodawaniu usługi:', err);
            setError('Błąd przy dodawaniu usługi.');
        }
    };

    // Handler for canceling add service
    const handleCancelAddService = () => {
        setNewService({ serviceName: '', price: '', duration: '' });
        setShowAddForm(false);
        setError('');
    };

    // Handler for editing an existing service
    const handleEditService = async (serviceId) => {
        if (!serviceId) return;
        const token = localStorage.getItem('jwt');
        const payload = {
            serviceId: serviceId,
            serviceName: editService.serviceName,
            price: parseFloat(editService.price),
            duration: parseDuration(editService.duration),
        };
        try {
            const response = await axios.patch(`/api/v1/services`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setServices(
                services.map((service) => (service.id === serviceId ? response.data : service))
            );
            setEditServiceId(null);
            setEditService({ serviceName: '', price: '', duration: '' });
            setError('');
        } catch (err) {
            console.error('Błąd przy edycji usługi:', err);
            setError('Błąd przy edycji usługi.');
        }
    };

    // Handler for deleting a service
    const handleDeleteService = async (serviceId) => {
        if (!serviceId) return;
        if (!window.confirm('Czy na pewno chcesz usunąć tę usługę?')) return;

        const token = localStorage.getItem('jwt');
        try {
            await axios.delete(`/api/v1/services/${serviceId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setServices(services.filter((service) => service.id !== serviceId));
            setError('');
        } catch (err) {
            console.error('Błąd przy usuwaniu usługi:', err);
            setError('Błąd przy usuwaniu usługi.');
        }
    };

    if (loading) {
        return (
            <div className="os-loading">
                <p>Ładowanie usług...</p>
            </div>
        );
    }

    return (
        <div className="os-container">
            {error && <div className="os-error-message">{error}</div>}

            {/* Empty State */}
            {services.length === 0 && !showAddForm ? (
                <div className="os-empty">
                    <div className="os-empty-icon">🎯</div>
                    <h3>Brak usług</h3>
                    <p>Nie ma jeszcze żadnych usług. Dodaj pierwszą usługę poniżej.</p>
                </div>
            ) : null}

            {/* Services Grid */}
            <div className="os-grid">
                {/* Existing Services */}
                {services.map((service) => (
                    <div key={service.id} className="os-card">
                        {editServiceId === service.id ? (
                            /* Edit Form */
                            <div className="os-edit-form">
                                <div className="os-form-row">
                                    <div className="os-input-group">
                                        <label>NAZWA USŁUGI</label>
                                        <input
                                            type="text"
                                            value={editService.serviceName}
                                            onChange={(e) =>
                                                setEditService({
                                                    ...editService,
                                                    serviceName: e.target.value,
                                                })
                                            }
                                            className="os-form-input"
                                            placeholder="np. Trening podstawowy"
                                        />
                                    </div>
                                </div>
                                <div className="os-form-row os-form-row--two-cols">
                                    <div className="os-input-group">
                                        <label>CENA (PLN)</label>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={editService.price}
                                            onChange={(e) =>
                                                setEditService({
                                                    ...editService,
                                                    price: e.target.value,
                                                })
                                            }
                                            className="os-form-input"
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <div className="os-input-group">
                                        <label>CZAS TRWANIA</label>
                                        <input
                                            type="time"
                                            value={editService.duration}
                                            onChange={(e) =>
                                                setEditService({
                                                    ...editService,
                                                    duration: e.target.value,
                                                })
                                            }
                                            className="os-form-input"
                                        />
                                    </div>
                                </div>
                                <div className="os-form-buttons">
                                    <button
                                        className="os-btn-save"
                                        onClick={() => handleEditService(service.id)}
                                    >
                                        Zapisz
                                    </button>
                                    <button
                                        className="os-btn-cancel"
                                        onClick={() => {
                                            setEditServiceId(null);
                                            setEditService({
                                                serviceName: '',
                                                price: '',
                                                duration: '',
                                            });
                                        }}
                                    >
                                        Anuluj
                                    </button>
                                </div>
                            </div>
                        ) : (
                            /* Display Mode */
                            <div className="os-card-display">
                                <div className="os-card-header">
                                    <div className="os-card-info">
                                        <h3 className="os-card-title">{service.serviceName}</h3>
                                    </div>
                                    <div className="os-card-actions">
                                        <button
                                            className="os-edit-btn"
                                            onClick={() => {
                                                setEditServiceId(service.id);
                                                setEditService({
                                                    serviceName: service.serviceName,
                                                    price: service.price,
                                                    duration: parseHumanDuration(service.duration),
                                                });
                                            }}
                                            title="Edytuj usługę"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M14.06 9.02l.92.92L5.92 19H5v-.92l9.06-9.06M17.66 3c-.25 0-.51.1-.7.29l-1.83 1.83 3.75 3.75 1.83-1.83c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29zm-3.6 3.19L3 17.25V21h3.75L17.81 9.94l-3.75-3.75z"/>
                                            </svg>
                                        </button>
                                        <button
                                            className="os-delete-btn"
                                            onClick={() => handleDeleteService(service.id)}
                                            title="Usuń usługę"
                                        >
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M19 4h-3.5l-1-1h-5l-1 1H5v2h14M6 19a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7H6v12z"/>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                <div className="os-card-details">
                                    <div className="os-detail-item">
                                        <span className="os-detail-label">Cena</span>
                                        <span className="os-price-value">{service.price} PLN</span>
                                    </div>
                                    <div className="os-detail-item">
                                        <span className="os-detail-label">Czas</span>
                                        <span className="os-duration-value">{service.duration}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {/* Add New Service Card */}
                <div className="os-add-card">
                    {!showAddForm ? (
                        /* Plus Icon State */
                        <div className="os-add-plus" onClick={() => setShowAddForm(true)}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                            </svg>
                        </div>
                    ) : (
                        /* Form State */
                        <div className="os-add-content">
                            <div className="os-form-row">
                                <div className="os-input-group">
                                    <label>NAZWA USŁUGI</label>
                                    <input
                                        type="text"
                                        value={newService.serviceName}
                                        onChange={(e) => setNewService({ ...newService, serviceName: e.target.value })}
                                        className="os-form-input"
                                        placeholder="np. Trening podstawowy"
                                    />
                                </div>
                            </div>
                            <div className="os-form-row os-form-row--two-cols">
                                <div className="os-input-group">
                                    <label>CENA (PLN)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={newService.price}
                                        onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                                        className="os-form-input"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div className="os-input-group">
                                    <label>CZAS TRWANIA</label>
                                    <input
                                        type="time"
                                        value={newService.duration}
                                        onChange={(e) => setNewService({ ...newService, duration: e.target.value })}
                                        className="os-form-input"
                                    />
                                </div>
                            </div>
                            <div className="os-form-buttons">
                                <button className="os-btn-save" onClick={handleAddService}>
                                    Dodaj usługę
                                </button>
                                <button className="os-btn-cancel" onClick={handleCancelAddService}>
                                    Anuluj
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default OfferedServices;